import { useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { Badge, completionVariant, completionLabel } from "./Badge";
import type { Requirement } from "@/types";

export interface TreeNodeProps {
  requirement: Requirement;
  children: Requirement[];
  allRequirements: Requirement[];
  getChildren: (driverId: string) => Requirement[];
  selectedId: string | null;
  highlightDriverIds: Set<string>;
  highlightDrivenIds: Set<string>;
  driverCountByReqId: Map<string, number>;
  depth?: number;
  expandedNodes: Set<string>;
  onToggle: (id: string) => void;
}

export function TreeNode({ requirement, children: childReqs, allRequirements, getChildren, selectedId, highlightDriverIds, highlightDrivenIds, driverCountByReqId, depth = 0, expandedNodes, onToggle }: TreeNodeProps) {
  const { navigateTo, activeId } = useApp();
  const isExpanded = expandedNodes.has(requirement.id);
  const hasChildren = childReqs.length > 0;
  const isActive = activeId === requirement.id;
  const isSelected = selectedId === requirement.id;
  const isDriver = highlightDriverIds.has(requirement.id);
  const isDriven = highlightDrivenIds.has(requirement.id);
  const driverCount = driverCountByReqId.get(requirement.id) || 0;
  const hasMultipleDrivers = driverCount > 1;
  const rowStateClass = isSelected
    ? "bg-blue-500/[0.16] ring-1 ring-blue-400/40"
    : isDriver
      ? "bg-yellow-500/[0.12] ring-1 ring-yellow-400/35"
      : isDriven
        ? "bg-green-500/[0.10] ring-1 ring-green-400/30"
        : "";

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(requirement.id);
  }, [onToggle, requirement.id]);

  const handleClick = useCallback(() => {
    navigateTo("requirements", requirement.id);
  }, [navigateTo, requirement.id]);

  return (
    <div data-testid={`tree-node-${requirement.id}`}>
      <div
        onClick={handleClick}
        className={`flex items-center gap-1.5 py-1 pr-2 rounded-default cursor-pointer transition-colors duration-100 ${rowStateClass}`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onMouseEnter={e => { if (!isSelected && !isDriver && !isDriven) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)"; }}
        onMouseLeave={e => { if (!isSelected && !isDriver && !isDriven) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
        data-testid={isSelected ? `tree-node-selected-${requirement.id}` : isDriver ? `tree-node-driver-${requirement.id}` : isDriven ? `tree-node-driven-${requirement.id}` : undefined}
      >
        {/* Toggle */}
        <span
          onClick={hasChildren ? handleToggle : undefined}
          className={`w-4 text-center text-[10px] ${hasChildren ? "text-text2 cursor-pointer" : "text-transparent cursor-default"}`}
        >
          {hasChildren ? (isExpanded ? "▼" : "▶") : "·"}
        </span>

        {/* ID */}
        <span className="font-mono text-xs text-accent font-semibold shrink-0">{requirement.id}</span>
        {hasMultipleDrivers && (
          <span
            className="text-[11px] text-yellow shrink-0"
            title={`Driven by ${driverCount} requirements`}
            aria-label="Requirement has multiple drivers"
          >
            🔗
          </span>
        )}
        {isSelected && <RelationBadge label="Selected" className="border-blue-400/50 text-blue" />}
        {isDriver && <RelationBadge label="Driver" className="border-yellow-400/50 text-yellow" />}
        {isDriven && <RelationBadge label="Driven" className="border-green-400/50 text-green" />}
        {requirement.title && (
          <span className="text-text text-xs overflow-hidden text-ellipsis whitespace-nowrap flex-1 min-w-0">- {requirement.title}</span>
        )}

        {/* Badges - fixed columns */}
        <div className="ml-auto flex shrink-0">
          <div className="w-[90px] flex justify-end px-1">
            {requirement.level && <Badge>{requirement.level}</Badge>}
          </div>
          <div className="w-[80px] flex justify-end px-1">
            {requirement.discipline && <Badge variant="info">{requirement.discipline}</Badge>}
          </div>
          <div className="w-[80px] flex justify-end px-1">
            {requirement.completion && <Badge variant={completionVariant(requirement.completion)}>{completionLabel(requirement.completion)}</Badge>}
          </div>
          <div className="w-[100px] flex justify-end gap-1 px-1 overflow-hidden">
            {requirement.tags && requirement.tags.length > 0 && requirement.tags.map(t => <Badge key={t} variant="muted">{t}</Badge>)}
          </div>
        </div>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div>
          {childReqs.map(child => (
            <TreeNode
              key={child.id}
              requirement={child}
              children={getChildren(child.id)}
              allRequirements={allRequirements}
              getChildren={getChildren}
              selectedId={selectedId}
              highlightDriverIds={highlightDriverIds}
              highlightDrivenIds={highlightDrivenIds}
              driverCountByReqId={driverCountByReqId}
              depth={depth + 1}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function RelationBadge({ label, className }: { label: string; className: string }) {
  return <span className={`text-[10px] px-1.5 py-px border rounded-full uppercase tracking-wide ${className}`}>{label}</span>;
}
